import React, { useEffect, useRef, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaCheck,
  FaCoins,
  FaCreditCard,
  FaExclamationTriangle,
  FaPauseCircle,
  FaSpinner,
} from "react-icons/fa";
import HeaderComponent from "../../Header";
import useBilling from "../hooks/useBilling";

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 40; // ~2 minutes

const PENDING_STATUSES = ["pending_approval", "approved"];
const SUBSCRIBED_STATUSES = ["active", "past_due"];

const formatDate = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
};

const Banner = ({ tone, icon: Icon, title, children }) => {
  const tones = {
    warning: "bg-yellow-50 border-yellow-300 text-yellow-800",
    danger: "bg-red-50 border-red-300 text-red-800",
    info: "bg-blue-50 border-blue-300 text-blue-800",
  };

  return (
    <div className={`border rounded-lg p-4 mb-6 flex items-start ${tones[tone]}`}>
      {Icon && <Icon className="mt-1 mr-3 flex-shrink-0" />}
      <div>
        <p className="font-semibold">{title}</p>
        {children && <p className="text-sm mt-1">{children}</p>}
      </div>
    </div>
  );
};

// The 70/90/100% nudges. Rendered wherever the meter is shown.
const UsageNudge = ({ usage, credits }) => {
  const limit = (usage && usage.limit) || 0;
  const used = (usage && usage.used) || 0;
  if (limit <= 0) return null;

  const percent = Math.round((used / limit) * 100);
  if (percent < 70) return null;

  if (percent >= 100) {
    return (
      <Banner tone="danger" icon={FaExclamationTriangle} title="You have used your full allowance">
        {credits > 0
          ? `New certificates are now drawing from your ${credits} purchased credits.`
          : "Upgrade your plan or buy a credit pack below to keep issuing certificates."}
      </Banner>
    );
  }

  if (percent >= 90) {
    return (
      <Banner tone="warning" icon={FaExclamationTriangle} title={`You have used ${percent}% of your allowance`}>
        Consider upgrading or adding a credit pack so issuance never stops.
      </Banner>
    );
  }

  return (
    <Banner tone="info" title={`You have used ${percent}% of your allowance this period`}>
      Your allowance resets when the period renews. Credits never expire.
    </Banner>
  );
};

const UsageMeter = ({ usage }) => {
  const limit = usage && usage.limit ? usage.limit : 0;
  const used = (usage && usage.used) || 0;
  const remaining = (usage && usage.remaining) || 0;
  const percent = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const exhausted = remaining === 0;

  const barColor = exhausted
    ? "bg-red-500"
    : percent >= 90
      ? "bg-orange-500"
      : percent >= 70
        ? "bg-yellow-500"
        : "bg-blue-600";

  return (
    <div className="mt-6">
      <div className="flex justify-between text-sm text-gray-600 mb-2">
        <span>
          {used} of {limit} certificates used this period
        </span>
        <span className={exhausted ? "font-semibold text-red-600" : "font-semibold text-gray-800"}>
          {remaining} remaining
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
        <div className={`h-3 rounded-full ${barColor}`} style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
};

const StatusBadge = ({ label, tone }) => {
  const tones = {
    green: "bg-green-100 text-green-800",
    yellow: "bg-yellow-100 text-yellow-800",
    red: "bg-red-100 text-red-800",
    gray: "bg-gray-100 text-gray-800",
  };

  return (
    <span className={`px-3 py-1 text-sm font-medium rounded-full ${tones[tone]}`}>{label}</span>
  );
};

const humanizeFeature = (key, value) => {
  const label = key.replace(/_/g, " ");
  if (value === true) return label;
  return `${label}: ${value}`;
};

// One rung of the ladder. `action` decides the button: subscribe, change, or none.
const PlanCard = ({ plan, isCurrent, action, actionLabel, loading, onAction }) => {
  const features = plan.features && typeof plan.features === "object" ? plan.features : {};

  return (
    <div
      className={`bg-white rounded-lg p-6 flex flex-col border ${
        isCurrent ? "border-blue-600 ring-2 ring-blue-100" : "border-gray-200"
      }`}
    >
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-gray-900">{plan.name}</h3>
        {isCurrent && <StatusBadge label="Current plan" tone="green" />}
      </div>
      <p className="mt-2 text-3xl font-bold text-gray-900">
        {plan.price}
        <span className="text-base font-normal text-gray-500">/{plan.interval}</span>
      </p>
      <ul className="mt-4 space-y-2 text-sm text-gray-700 flex-1">
        <li className="flex items-start">
          <FaCheck className="text-green-600 mt-0.5 mr-2 flex-shrink-0" />
          {plan.certificate_limit} certificates per {plan.interval}
        </li>
        {Object.entries(features)
          .slice(0, 4)
          .map(([key, value]) => (
            <li key={key} className="flex items-start capitalize">
              <FaCheck className="text-green-600 mt-0.5 mr-2 flex-shrink-0" />
              {humanizeFeature(key, value)}
            </li>
          ))}
      </ul>
      {action !== "none" && (
        <button
          type="button"
          onClick={onAction}
          disabled={loading || isCurrent || !plan.available}
          className={`mt-6 w-full flex items-center justify-center py-2.5 px-4 font-semibold rounded-md transition disabled:opacity-60 disabled:cursor-not-allowed ${
            isCurrent
              ? "border border-gray-300 text-gray-500"
              : "bg-blue-600 text-white hover:bg-blue-700"
          }`}
        >
          {loading ? <FaSpinner className="animate-spin mr-2" /> : <FaCreditCard className="mr-2" />}
          {isCurrent ? "Current plan" : plan.available ? actionLabel : "Coming soon"}
        </button>
      )}
    </div>
  );
};

// action: "subscribe" (no active sub) | "change" (active sub, revise in place)
const PlanLadder = ({ plans, currentTier, action, loading, subscribe, changePlan }) => {
  if (!plans || !plans.length) return null;

  const currentIndex = plans.findIndex((p) => p.tier === currentTier);

  return (
    <div className="mt-8">
      <h2 className="text-xl font-bold text-gray-900 mb-4">
        {action === "change" ? "Change plan" : "Choose a plan"}
      </h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {plans.map((plan, index) => {
          const isCurrent = plan.tier === currentTier;
          let label = "Subscribe with PayPal";
          if (action === "change") {
            label = currentIndex >= 0 && index < currentIndex ? "Downgrade" : "Upgrade";
          }

          return (
            <PlanCard
              key={plan.tier || plan.name}
              plan={plan}
              isCurrent={isCurrent}
              action={action}
              actionLabel={label}
              loading={loading}
              onAction={() =>
                action === "change" ? changePlan(plan.tier) : subscribe(plan.tier)
              }
            />
          );
        })}
      </div>
      {action === "change" && (
        <p className="mt-3 text-xs text-gray-500">
          Upgrades raise your allowance immediately. Downgrades apply from your next billing period.
          You will be taken to PayPal to approve the change.
        </p>
      )}
    </div>
  );
};

const CreditsSection = ({ credits, packs, loading, buyCredits }) => (
  <div className="mt-8 bg-white shadow rounded-lg p-6">
    <div className="flex items-center justify-between">
      <div className="flex items-center">
        <FaCoins className="text-yellow-500 mr-3 text-xl" />
        <div>
          <h2 className="text-lg font-bold text-gray-900">Credits</h2>
          <p className="text-sm text-gray-500">
            Spent automatically after your period allowance. Never expire.
          </p>
        </div>
      </div>
      <p className="text-2xl font-bold text-gray-900">{credits}</p>
    </div>

    {packs && packs.length > 0 && (
      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        {packs.map((pack) => (
          <button
            key={pack.key}
            type="button"
            onClick={() => buyCredits(pack.key)}
            disabled={loading}
            className="border border-gray-300 rounded-md py-3 px-4 text-center hover:border-blue-500 hover:bg-blue-50 disabled:opacity-60 disabled:cursor-not-allowed transition"
          >
            <p className="font-semibold text-gray-900">{pack.credits} credits</p>
            <p className="text-sm text-gray-500">{pack.price}</p>
          </button>
        ))}
      </div>
    )}
    <p className="mt-3 text-xs text-gray-500">You will be taken to PayPal to complete the purchase.</p>
  </div>
);

const App = (props) => {
  const { billing, loading, subscribe, changePlan, buyCredits, cancel, fetchStatus } = useBilling(
    props.billing || null
  );
  const [polling, setPolling] = useState(false);
  const noticeShown = useRef(false);

  const status = (billing && billing.status) || "none";
  const plan = billing && billing.plan;
  const plans = (billing && billing.plans) || [];
  const packs = (billing && billing.credit_packs) || [];
  const creditBalance = (billing && billing.credits && billing.credits.balance) || 0;
  const usage = billing && billing.usage;
  const enforcement = !!(billing && billing.enforcement);
  const paidThrough = !!(billing && billing.paid_through);
  const periodEnd = formatDate(billing && billing.current_period_end);
  const subscribed = SUBSCRIBED_STATUSES.includes(status);
  const currentTier = billing && billing.tier;

  // One-shot notices from PayPal redirects: ?cancelled=1 and ?credits_added=N.
  useEffect(() => {
    if (noticeShown.current) return;
    noticeShown.current = true;

    const params = new URLSearchParams(window.location.search);
    if (params.get("cancelled") === "1") {
      toast.info("Checkout cancelled — you have not been charged.", {
        position: "bottom-center",
        autoClose: 5000,
      });
    }
    const added = parseInt(params.get("credits_added"), 10);
    if (added > 0) {
      toast.success(`${added} credits added to your account.`, {
        position: "bottom-center",
        autoClose: 6000,
      });
    }
  }, []);

  // While PayPal finishes activating the subscription, poll until it lands.
  useEffect(() => {
    if (!PENDING_STATUSES.includes(status)) {
      setPolling(false);
      return undefined;
    }

    setPolling(true);
    let attempts = 0;

    const timer = window.setInterval(async () => {
      attempts += 1;
      const fresh = await fetchStatus();

      if (attempts >= POLL_MAX_ATTEMPTS || (fresh && !PENDING_STATUSES.includes(fresh.status))) {
        window.clearInterval(timer);
        setPolling(false);
      }
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Pre-ladder fallback: a single configured plan and the plain subscribe flow.
  const renderSinglePlanCard = (label) => (
    <div className="bg-white shadow rounded-lg p-8">
      <div className="flex items-baseline justify-between">
        <h2 className="text-2xl font-bold text-gray-900">{plan.name}</h2>
        <p className="text-3xl font-bold text-gray-900">
          {plan.price}
          <span className="text-base font-normal text-gray-500">/{plan.interval}</span>
        </p>
      </div>
      <ul className="mt-6 space-y-3 text-gray-700">
        <li className="flex items-start">
          <FaCheck className="text-green-600 mt-1 mr-3 flex-shrink-0" />
          Generate up to {plan.certificate_limit} certificates per {plan.interval}
        </li>
        <li className="flex items-start">
          <FaCheck className="text-green-600 mt-1 mr-3 flex-shrink-0" />
          Bulk CSV import and email delivery
        </li>
        <li className="flex items-start">
          <FaCheck className="text-green-600 mt-1 mr-3 flex-shrink-0" />
          Cancel anytime — you keep access until the end of the period you paid for
        </li>
      </ul>
      <button
        type="button"
        onClick={() => subscribe()}
        disabled={loading}
        className="mt-8 w-full flex items-center justify-center py-3 px-4 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition"
      >
        {loading ? <FaSpinner className="animate-spin mr-2" /> : <FaCreditCard className="mr-2" />}
        {loading ? "Starting checkout…" : label}
      </button>
      <p className="mt-3 text-xs text-center text-gray-500">
        You will be taken to PayPal to approve the subscription.
      </p>
    </div>
  );

  const renderChooser = (subscribeLabelWhenSingle) =>
    plans.length > 0 ? (
      <PlanLadder
        plans={plans}
        currentTier={subscribed ? currentTier : null}
        action="subscribe"
        loading={loading}
        subscribe={subscribe}
        changePlan={changePlan}
      />
    ) : plan && plan.tier !== "free" ? (
      renderSinglePlanCard(subscribeLabelWhenSingle)
    ) : (
      <div className="bg-white shadow rounded-lg p-8 text-center">
        <h2 className="text-xl font-semibold text-gray-800">Subscriptions are not available yet</h2>
        <p className="text-gray-500 mt-2">Please check back shortly.</p>
      </div>
    );

  const renderBody = () => {
    if (PENDING_STATUSES.includes(status)) {
      return (
        <div className="bg-white shadow rounded-lg p-8 text-center py-10">
          <FaSpinner className="animate-spin mx-auto text-3xl text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-800 mt-4">Finishing setup with PayPal…</h2>
          <p className="text-gray-500 mt-2">
            This usually takes a few seconds. {polling ? "Checking for updates…" : ""}
          </p>
          <button
            type="button"
            onClick={fetchStatus}
            className="mt-6 text-blue-600 hover:underline text-sm"
          >
            Check now
          </button>
        </div>
      );
    }

    if (subscribed) {
      return (
        <>
          {status === "past_due" && (
            <Banner tone="warning" icon={FaExclamationTriangle} title="Payment failed">
              PayPal will retry automatically. Update your payment method in your PayPal account to
              avoid losing access.
            </Banner>
          )}

          <UsageNudge usage={usage} credits={creditBalance} />

          <div className="bg-white shadow rounded-lg p-8">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{plan ? plan.name : "Subscription"}</h2>
                <p className="text-gray-500 mt-1">
                  {plan ? `${plan.price}/${plan.interval}` : ""}
                  {periodEnd ? ` — renews on ${periodEnd}` : ""}
                </p>
              </div>
              <StatusBadge
                label={status === "active" ? "Active" : "Past due"}
                tone={status === "active" ? "green" : "yellow"}
              />
            </div>

            <UsageMeter usage={usage} />

            <button
              type="button"
              onClick={cancel}
              disabled={loading}
              className="mt-8 w-full py-3 px-4 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 disabled:opacity-60 transition"
            >
              Cancel subscription
            </button>
          </div>

          {plans.length > 0 && (
            <PlanLadder
              plans={plans}
              currentTier={plan && plan.tier}
              action="change"
              loading={loading}
              subscribe={subscribe}
              changePlan={changePlan}
            />
          )}

          <CreditsSection
            credits={creditBalance}
            packs={packs}
            loading={loading}
            buyCredits={buyCredits}
          />
        </>
      );
    }

    if (status === "suspended") {
      return (
        <>
          <Banner tone="danger" icon={FaPauseCircle} title="Subscription suspended">
            Certificate generation is paused. Start a new subscription to continue.
          </Banner>
          <div className="bg-white shadow rounded-lg p-8">
            <UsageMeter usage={usage} />
          </div>
          {renderChooser("Resubscribe with PayPal")}
          <CreditsSection
            credits={creditBalance}
            packs={packs}
            loading={loading}
            buyCredits={buyCredits}
          />
        </>
      );
    }

    if (status === "cancelled" && paidThrough) {
      return (
        <>
          <Banner tone="info" icon={FaExclamationTriangle} title="Subscription cancelled">
            You keep access until {periodEnd || "the end of your paid period"}.
          </Banner>
          <div className="bg-white shadow rounded-lg p-8">
            <UsageMeter usage={usage} />
          </div>
          {renderChooser("Resubscribe with PayPal")}
          <CreditsSection
            credits={creditBalance}
            packs={packs}
            loading={loading}
            buyCredits={buyCredits}
          />
        </>
      );
    }

    // free, none, expired, or cancelled past the paid period.
    return (
      <>
        <UsageNudge usage={usage} credits={creditBalance} />
        {usage && usage.limit > 0 && (
          <div className="bg-white shadow rounded-lg p-8">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Free plan</h2>
                <p className="text-gray-500 mt-1 text-sm">
                  {usage.limit} free certificates per month. Upgrade for more.
                </p>
              </div>
              <StatusBadge label="Free" tone="gray" />
            </div>
            <UsageMeter usage={usage} />
          </div>
        )}
        {renderChooser(status === "none" || status === "free" ? "Subscribe with PayPal" : "Resubscribe with PayPal")}
        <CreditsSection
          credits={creditBalance}
          packs={packs}
          loading={loading}
          buyCredits={buyCredits}
        />
      </>
    );
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <HeaderComponent />
      <div className="container mx-auto py-8 px-4 max-w-4xl">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Billing</h1>

        {!enforcement && (
          <Banner tone="info" title="Early access — billing is not yet enforced">
            Certificate generation is not limited yet. You can subscribe now to be ready when it is.
          </Banner>
        )}

        {renderBody()}
      </div>
      <ToastContainer />
    </div>
  );
};

export default App;
